// CMMICertification.jsx
import React from "react";

export default function CMMICertification() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            CMMI Certification
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            CMMI Certification for IT Organizations
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks assists IT and software organizations with CMMI
            Certification to demonstrate mature process capability and global
            competitiveness in software development and service delivery.
          </p>
          <p className="text-gray-700">
            From gap analysis and process definition through SCAMPI appraisal
            preparation and authorized lead appraiser coordination, the journey
            to CMMI Level 3, 5 or higher maturity certification is structured
            for measurable process improvement and client confidence.
          </p>
        </div>

        {/* Right: Image (CMMI CERTIFICATION - IT software development team, professional coding, process maturity) */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/1181244/pexels-photo-1181244.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="IT organization achieving CMMI Certification"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* PROFESSIONAL SUPPORT & DOCUMENTS SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Professional compliance support */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Professional Compliance Support by Hawkins &amp; Brooks
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks provides end-to-end CMMI implementation and
              certification support from initial gap assessment through SCAMPI
              appraisal success.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  CMMI model selection (Dev, Services, Acquisition) and target
                  maturity/capability level gap analysis against current processes.
                </li>
                <li>
                  Process definition and documentation across 22 process areas
                  including project planning, configuration management, and peer reviews.
                </li>
                <li>
                  SCAMPI A appraisal preparation with authorized lead appraiser
                  coordination, mock appraisals, and finding resolution.
                </li>
                <li>
                  Post-certification sustainment planning and process performance
                  analysis for continuous improvement and re-appraisal readiness.
                </li>
              </ul>
            </div>
          </div>

          {/* Documents required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for CMMI Certification
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks helps develop and organize CMMI process
              documentation required for appraisal team review and certification.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>Process documentation including policies, procedures, work instructions, and templates for 22 CMMI process areas.</li>
                <li>
                  Organizational details such as process asset library, training
                  records, and organizational process focus documentation.
                </li>
                <li>
                  Quality records including project management plans, peer review
                  reports, configuration management records, and measurement data.
                </li>
                <li>
                  Evidence of process performance such as process &amp; product
                  quality assurance reports and causal analysis records.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Why choose us */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The firm delivers proven CMMI implementation expertise with
              successful Level 3 and Level 5 certifications across IT services.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  IT Process Compliance Expertise
                </h3>
                <p className="text-gray-700 text-sm">
                  Deep CMMI V2.0 knowledge across Development, Services, and
                  Acquisition models with experience in Levels 3, 4, and 5 appraisals.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Structured Certification Support
                </h3>
                <p className="text-gray-700 text-sm">
                  Phased implementation approach with gap analysis, process
                  development, mock appraisals, and SCAMPI A appraisal coordination.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Professional Advisory Services
                </h3>
                <p className="text-gray-700 text-sm">
                  End-to-end guidance from charter creation through certification
                  sustainment with performance analytics and re-appraisal planning.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col justify-center">
            <div className="bg-gray-50 border border-yellow-100 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Achieve CMMI Certification Success
              </h3>
              <p className="text-gray-700 mb-5">
                Connect with Hawkins &amp; Brooks to implement CMMI processes and
                achieve globally recognized maturity certification that enhances
                client trust and competitive positioning in IT services.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
                >
                  Get Consultation
                </a>
                <a
                  href="tel:+919217468906"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm"
                >
                  Call Hawkins &amp; Brooks
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
